<template>
	<div
		v-if="basket"
		class="main-wrapper relative my-10 flex flex-col gap-6 lg:gap-8"
	>
		<h1
			class="mb-4 flex items-center gap-2 text-[28px] text-gray-700 font-bold uppercase"
		>
			<LockClosedIcon class="w-7 h-7" />
			{{ $t("Checkout") }}
		</h1>
		<div
			v-if="getIsLoading"
			class="absolute inset-0 z-50 bg-gray-100 bg-opacity-60 flex items-center justify-center h-auto"
		>
			<div
				class="inline-block h-5 w-5 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
				role="status"
			></div>
		</div>
		<div class="flex flex-col gap-6">
			<h2
				class="checkout-basket-heading text-[22px] text-gray-700 font-bold uppercase"
			>
				{{ $t("Shopping bag") }}
			</h2>
			<SharedSharedBasket :basket="basket" :order-id="orderId" />
			<div class="mx-auto flex w-full max-w-[700px] flex-col gap-6 lg:gap-8">
				<CheckoutMarketingOptOut
					v-if="isMarketingOptOutEnabled"
					:order="order"
					class="w-full"
				/>
				<!-- Waits for the confirmed type as well: showing the radios on the
				assumed one would flip the selection under the shopper a moment later. -->
				<CheckoutChangeCustomerType
					v-if="orderId && hasCheckoutChannels && isCustomerTypeConfirmed"
					:model-value="customerType"
					:disabled="isSwitchingCheckout"
					class="w-full"
					@update:model-value="handleCustomerTypeChange"
				/>
				<!-- The condition sits on the wrapper: an empty wrapper would still be a
				flex item and add the section gap around nothing. -->
				<div
					v-if="ShippingComponent && basket && orderId && order"
					class="w-full"
				>
					<component
						:is="ShippingComponent"
						:basket="basket"
						:order-id="orderId"
						:order="order"
						:update="update"
						@delivery-updated="onDeliveryUpdated"
						@recreate-order="handleRecreateOrder"
					/>
				</div>
				<!-- [NF-385] Self-gates on public.checkoutComment.enabled; renders
				nothing otherwise. Persists to basket.Comment on blur, so the note is
				on the Norce basket before the shopper completes payment. -->
				<CheckoutComment class="w-full" />
				<CheckoutPaymentAdapterSelect
					v-if="hasAdapterChoice"
					:model-value="activeAdapterName"
					:adapters="paymentAdapterList"
					:disabled="isSwitchingCheckout"
					class="w-full"
					@update:model-value="handlePaymentAdapterChange"
				/>
				<!-- [NF-385] commentSatisfied gates the payment step when the comment
				is required: the adapter stays unmounted (so it can't create a payment
				or show a pay button) until a non-empty comment is saved. Always true
				when the comment is optional or disabled. -->
				<div
					v-if="basket && orderId && canMountPaymentAdapter && commentSatisfied"
					class="w-full"
				>
					<component
						:is="PaymentComponent"
						:key="activeAdapterName"
						:basket="basket"
						:order-id="orderId"
						:update="update"
						@recreate-order="handleRecreateOrder"
					/>
				</div>
				<!-- [NF-385] A required comment gates the adapter above; without this a
				shopper scrolling to payment finds it empty and unexplained. -->
				<p
					v-else-if="
						basket && orderId && canMountPaymentAdapter && !commentSatisfied
					"
					class="w-full text-sm text-red-600"
				>
					{{ $t("checkout.comment.required_to_pay") }}
				</p>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { onMounted, ref } from "vue";
import type { Order } from "@nwt/norce-pwa/dist/runtime/types/api/checkout/orders/orders";
import type { Locale as AppLocale } from "~/types/locale";
import { LockClosedIcon } from "@heroicons/vue/24/solid";
// The presentational children (SharedSharedBasket, CheckoutChangeCustomerType,
// CheckoutPaymentAdapterSelect) are auto-imported on purpose: a static relative
// import from a base file always resolves inside base, which would make client
// layers unable to re-skin them. The payment/shipping adapters stay on relative
// dynamic imports because they carry logic and must not be forked.
import { useGtmEvents } from "~/layers/base/composables/useGtmEvents";

const config = useRuntimeConfig();
const { locale } = useI18n();
const _locales = (config.public.norce.locales ?? []) as AppLocale[];
const _localeConfig = _locales.find((l) => l.code === locale.value);
const adapterComponentName =
	_localeConfig?.paymentAdapter ?? config.public.norce.paymentAdapter;
const isMarketingOptOutEnabled = Boolean(
	config.public.checkout?.marketingOptOut
);

// Per-customer-type checkout: present as soon as either type is configured,
// in the channel-only string form or the full object form.
const hasCheckoutChannels = Boolean(
	_localeConfig?.checkoutChannels?.person ||
	_localeConfig?.checkoutChannels?.organization
);

const norceStore = useNorceStore();
const {
	ensureCheckoutChannel,
	getCheckoutQueryParams,
	getCustomerTypeForChannel,
	getDefaultPaymentAdapterForType,
	getPaymentAdaptersForType,
	resolveOrderByChannel,
	setCheckoutChannelForType
} = useCheckoutChannel();

// Declared up here because the provider list below is resolved per customer
// type. The switch itself stays further down, with the order lifecycle.
//
// Seeded from the persisted checkout channel: the order's `customer.type` is
// authoritative but needs a round trip, and starting on the wrong type would
// briefly offer the wrong providers on a reload of a B2B checkout.
const customerType = ref<"person" | "organization">(
	getCustomerTypeForChannel(norceStore.checkoutChannel) ?? "person"
);
const isSwitchingCustomerType = ref(false);
const isSwitchingPaymentAdapter = ref(false);
// Both switches delete and recreate the order, so they must never overlap: the
// second would delete the order the first just created and then bail out on
// placeOrder()'s isSyncingCheckout guard, leaving checkout with no order at all.
const isSwitchingCheckout = computed(
	() => isSwitchingCustomerType.value || isSwitchingPaymentAdapter.value
);
// Flipped once the first order lookup has settled, i.e. once `customerType`
// comes from Norce rather than from the seed above. Payment adapters create
// their payment in `onMounted`, so mounting one on an unconfirmed type sends it
// at the wrong channel (Klarna on a B2B channel → 500, then 409 on the retry).
const isCustomerTypeConfirmed = ref(false);

// Payment providers are enabled per NCO channel, and B2C/B2B sit on different
// channels — so the list follows the customer type:
// `checkoutChannels[type].paymentAdapters` first, then the locale-wide
// `paymentAdapters`. Empty means no list is configured.
const paymentAdapterList = computed(() =>
	getPaymentAdaptersForType(customerType.value)
);
const hasAdapterChoice = computed(() => paymentAdapterList.value.length > 1);

const { selected: selectedPaymentAdapter, normalize: normalizePaymentAdapter } =
	usePaymentAdapterSelection(paymentAdapterList, {
		defaultAdapter: () => getDefaultPaymentAdapterForType(customerType.value)
	});

// With a choice, the mounted adapter follows the shopper's selection. With a
// one-entry list it is that entry — a customer type configured with a single
// provider gets its own, not the locale-wide one. Only an unset list falls back
// to `paymentAdapter` / PUBLIC_NORCE_PAYMENT_ADAPTER.
const activeAdapterName = computed(() =>
	hasAdapterChoice.value
		? selectedPaymentAdapter.value
		: (paymentAdapterList.value[0] ?? adapterComponentName)
);

// Which adapter is correct depends on the customer type, so on a locale with
// per-type channels nothing may mount until the type is confirmed. Locales
// without them have a single set of providers and nothing to wait for.
const canMountPaymentAdapter = computed(
	() => !hasCheckoutChannels || isCustomerTypeConfirmed.value
);
// Read `activeAdapterName.value` synchronously here so the computed actually
// depends on it — reading it only inside the async loader would not register a
// dependency, so the computed would never re-evaluate and the mounted adapter
// would never change on switch.
const PaymentComponent = computed(() => {
	const name = activeAdapterName.value;
	return defineAsyncComponent(() => import(`./PaymentAdapter/${name}.vue`));
});
// Same precedence as the payment adapter above: per-locale first, then the
// global env fallback documented in docs/PAYMENT-PROVIDERS.md.
const shippingAdapterName =
	_localeConfig?.shippingAdapter ?? config.public.norce.shippingAdapter;
const ShippingComponent = shippingAdapterName
	? defineAsyncComponent(
			() => import(`./ShippingAdapter/${shippingAdapterName}.vue`)
		)
	: null;

const cartStore = useCartStore();
const { basket } = storeToRefs(cartStore);
const { displayBasket } = useDisplayBasket();
// [NF-385] false until a required comment is saved; always true otherwise.
const { satisfied: commentSatisfied } = useCheckoutComment();

const {
	norceAdapter,
	norceOrders,
	paymentAdapter,
	updateBasket,
	updateBasketPaymentMethod,
	updateDeliveryMethod
} = useNorcePwa();
const { getIsLoading } = storeToRefs(useHelpersStore());
const { setIsLoading } = useHelpersStore();
const { beginCheckout } = useGtmEvents();
const { checkout: dotdigitalCheckout } = useDotdigital();
const { withIngridSuspended } = useIngridSuspend();

const NorceCheckoutV2PaymentMethodId = 239;
const orderId = ref("");
const update = ref("");
const order = ref<Order | null>(null);
const isRecreatingOrder = ref(false);
const isDeliveryRefreshing = ref(false);
const isSyncingCheckout = ref(false);
let pendingDeliveryUpdate = false;
let lastCultureCode = norceStore.storeContext.cultureCode;
let lastBasketId = basket.value?.Id;

const buildUpdateToken = (reason: "cart" | "delivery") =>
	`${reason}:${Date.now()}`;

const loadOrder = async (targetOrderId: string) => {
	const nextOrder = await resolveOrderByChannel(targetOrderId);
	order.value = (nextOrder ?? null) as Order | null;
	// A freshly created order has no customer yet, so only an actual value from
	// Norce overrides the locally selected type.
	const loadedType = order.value?.customer?.type;
	if (loadedType === "person" || loadedType === "organization") {
		customerType.value = loadedType;
		// The persisted selection can belong to the other customer type (chosen in
		// an earlier session), and this type may not offer it at all — reconcile it
		// with this type's list, without overriding a choice that is still valid.
		normalizePaymentAdapter();
	}
	// Either Norce named the type, or the order has no customer yet — a freshly
	// created one, where the locally selected type is already the truth. Both
	// mean the payment adapter may now mount.
	isCustomerTypeConfirmed.value = true;
	return nextOrder ?? null;
};

// Best-effort teardown: the order belongs to the channel/adapter being left, so
// a failure here must not block building the replacement.
const deleteCurrentOrder = async (reason: string) => {
	if (!orderId.value) return;
	await norceOrders
		.deleteOrder({
			orderId: orderId.value.toString(),
			...getCheckoutQueryParams()
		})
		.catch((err) => console.error(`[Checkout] deleteOrder on ${reason}:`, err));
};

// Clearing orderId + OrderReference unmounts the payment/shipping adapters and
// stops placeOrder() from short-circuiting on the order that was just dropped.
const resetOrderRefs = () => {
	orderId.value = "";
	order.value = null;
	if (basket.value) basket.value.OrderReference = "";
};

const emitCheckoutUpdate = async (reason: "cart" | "delivery") => {
	if (!orderId.value || !basket.value?.Id) return;
	if (isSyncingCheckout.value) return;
	isSyncingCheckout.value = true;
	try {
		await withIngridSuspended(async () => {
			try {
				await norceAdapter.updateOrder({
					orderId: orderId.value,
					...getCheckoutQueryParams()
				});
				const currentOrder = await norceOrders.getOrderById({
					orderId: orderId.value,
					...getCheckoutQueryParams()
				});
				if (currentOrder) order.value = currentOrder as Order;
				const klarnaPayment = currentOrder?.payments?.find(
					(p) => p.adapterId === "klarna_checkout_adapter" && p.id
				);
				if (klarnaPayment?.id) {
					await paymentAdapter.updatePayment({
						orderId: orderId.value,
						paymentId: klarnaPayment.id,
						flag: PaymentAdapters.KLARNA,
						...getCheckoutQueryParams()
					});
				}
				update.value = buildUpdateToken(reason);
			} catch (err: any) {
				const statusMessage = err?.statusMessage || err?.data?.statusMessage;
				if (
					statusMessage === "klarna-order-not-found" ||
					statusMessage === "order-bad-request"
				) {
					setIsLoading(true);
					await norceOrders
						.deleteOrder({
							orderId: orderId.value,
							...getCheckoutQueryParams()
						})
						.catch(() => {});
					const orderBody = createOrderBody();
					const { id } = await norceAdapter.createOrder(orderBody);
					orderId.value = id;
					if (basket.value) {
						await updateBasket(norceStore.storeContext, {
							Id: basket.value.Id,
							OrderReference: id
						});
					}
					await loadOrder(id);
					setIsLoading(false);
					return;
				}
				console.error("[Checkout] order update failed:", err);
			}
		});
	} finally {
		isSyncingCheckout.value = false;
	}
};

const onDeliveryUpdated = async (newOrderId?: string) => {
	if (newOrderId) orderId.value = newOrderId;
	if (isDeliveryRefreshing.value) {
		pendingDeliveryUpdate = true;
		return;
	}
	isDeliveryRefreshing.value = true;
	try {
		await withIngridSuspended(async () => {
			if (orderId.value) await loadOrder(orderId.value);
			await emitCheckoutUpdate("delivery");
		});
	} finally {
		isDeliveryRefreshing.value = false;
		if (pendingDeliveryUpdate) {
			pendingDeliveryUpdate = false;
			onDeliveryUpdated();
		}
	}
};

const placeOrder = async () => {
	if (isSyncingCheckout.value) return;
	isSyncingCheckout.value = true;
	try {
		const basketId = basket?.value?.Id;
		// Opt-in per locale: seed the delivery method when the basket has none, so
		// the shopper doesn't have to pick one. Only when it's unset — a method the
		// shopper (or the shipping adapter) already chose is never overwritten.
		const defaultDeliveryMethodId = _localeConfig?.defaultDeliveryMethodId;
		if (
			defaultDeliveryMethodId &&
			basketId &&
			!basket.value?.DeliveryMethodId
		) {
			await updateDeliveryMethod({
				basketId: String(basketId),
				deliveryMethodId: String(defaultDeliveryMethodId),
				...norceStore.storeContext
			});
		}
		if (orderId.value) {
			await loadOrder(orderId.value);
			return;
		}

		const orderReference = basket?.value?.OrderReference;
		if (orderReference) {
			orderId.value = orderReference;
			await loadOrder(orderReference);
			return;
		}

		// Same reason as breadandboxers: the cart resolves after mount and
		// children mount before the layout, so the basket can be missing on the
		// first pass. Bail out rather than create an order without a basket — the
		// `basket` watcher re-runs placeOrder once it's there.
		if (!basketId) return;

		if (!basket.value?.PaymentMethodId) {
			await updateBasketPaymentMethod({
				basketId,
				paymentMethodId: NorceCheckoutV2PaymentMethodId,
				localeCode: locale.value
			});
		}

		setIsLoading(true);
		const orderBody = createOrderBody();
		const { id } = await norceAdapter.createOrder(orderBody);
		orderId.value = id;
		await updateBasket(norceStore.storeContext, {
			Id: basketId,
			OrderReference: id
		});
		await loadOrder(id);
		setIsLoading(false);
	} catch (error) {
		console.error("Error placing order:", error);
		if (basket.value) {
			setIsLoading(true);
			const orders = await norceOrders.getOrders({
				cart_reference: basket.value.Id?.toString(),
				...getCheckoutQueryParams()
			});
			const currentOrder = orders[0];
			if (currentOrder) {
				orderId.value = currentOrder.id;
				order.value = currentOrder as Order;
				await updateBasket(norceStore.storeContext, {
					Id: basket.value.Id,
					OrderReference: currentOrder.id
				});
			} else {
				const orderBody = createOrderBody();
				const { id } = await norceAdapter.createOrder(orderBody);
				orderId.value = id;
				await updateBasket(norceStore.storeContext, {
					Id: basket.value.Id,
					OrderReference: id.toString()
				});
			}
			setIsLoading(false);
		}
	} finally {
		isSyncingCheckout.value = false;
		// Also set here, not only in loadOrder(): the recovery path above can end
		// with a brand-new order without going through it, and leaving this false
		// would keep the payment adapter unmounted for good.
		isCustomerTypeConfirmed.value = true;
	}
};

const createOrderBody = () => {
	const basketId = basket.value?.Id;
	const currencyCode = norceStore?.getCurrentCurrency?.Code;
	const defaultCulture =
		norceStore.applicationNorce?.Cultures?.Default?.Code ??
		norceStore.cultureCode;
	const defaultCountry =
		norceStore.applicationNorce?.Countries?.Default?.Code ??
		defaultCulture?.split("-")[1];

	if (basketId && currencyCode && defaultCulture && defaultCountry) {
		return {
			cartReference: basketId,
			culture: defaultCulture,
			currency: currencyCode,
			country: defaultCountry,
			localeCode: locale.value,
			checkoutChannel:
				norceStore.checkoutChannel || ensureCheckoutChannel("person")
		};
	} else {
		throw new Error("Basket, currency, culture, or country is missing.");
	}
};

// Adapters (payment + shipping) don't own the order: when they find it unusable
// (a removed payment/shipping) they emit and this rebuilds it once, here.
const handleRecreateOrder = async (reason: string) => {
	if (isRecreatingOrder.value || isSyncingCheckout.value) return;
	isRecreatingOrder.value = true;
	setIsLoading(true);
	try {
		await deleteCurrentOrder(`${reason} failed`);
		resetOrderRefs();
		await placeOrder();
		if (orderId.value) await emitCheckoutUpdate("cart");
	} finally {
		isRecreatingOrder.value = false;
		setIsLoading(false);
	}
};

const handleCustomerTypeChange = async (value: string) => {
	const nextType = value === "organization" ? "organization" : "person";
	if (nextType === customerType.value || isSwitchingCheckout.value) {
		return;
	}
	isSwitchingCustomerType.value = true;
	setIsLoading(true);
	try {
		// Delete on the *previous* channel's query params, before switching.
		await deleteCurrentOrder("customer type switch failed");
		customerType.value = nextType;
		setCheckoutChannelForType(nextType);
		// The new type's NCO channel enables its own providers, so apply that
		// type's default instead of carrying over a choice made for the previous
		// one. Must run before placeOrder(): resetOrderRefs() unmounts the payment
		// component, and whichever one remounts once the order exists is the one
		// that creates the payment on it.
		normalizePaymentAdapter(true);
		resetOrderRefs();
		// placeOrder() is the only place that creates an order — it now runs on
		// the channel the new customer type maps to.
		await placeOrder();
		if (orderId.value) await emitCheckoutUpdate("cart");
	} finally {
		isSwitchingCustomerType.value = false;
		setIsLoading(false);
	}
};

const handlePaymentAdapterChange = async (name: string) => {
	if (name === selectedPaymentAdapter.value || isSwitchingCheckout.value) {
		return;
	}
	isSwitchingPaymentAdapter.value = true;
	setIsLoading(true);
	try {
		// Delete the current order (clears its payments and shippings), then let
		// placeOrder() build a fresh one.
		await deleteCurrentOrder("adapter switch failed");
		selectedPaymentAdapter.value = name;
		resetOrderRefs();
		await placeOrder();
		if (orderId.value) await emitCheckoutUpdate("cart");
	} finally {
		isSwitchingPaymentAdapter.value = false;
		setIsLoading(false);
	}
};

onBeforeMount(() => {
	setIsLoading(true);
});

onMounted(async () => {
	await placeOrder();
	// A removed payment leaves the order unusable, so drop it and build a fresh
	// one (this reconciliation used to live inside ChangeCustomerType).
	if (
		order.value?.payments?.length &&
		order.value.payments?.[0]?.state === "removed"
	) {
		await deleteCurrentOrder("removed payment reconciliation failed");
		resetOrderRefs();
		await placeOrder();
	}
	if (orderId.value) {
		await emitCheckoutUpdate("cart");
	}
	setIsLoading(false);
});

onMounted(() => {
	// The displayed basket, so the payload matches the rows — same as the cart
	// badge and `cart_state`.
	if (displayBasket.value?.Items?.length) {
		beginCheckout(displayBasket.value);
		dotdigitalCheckout(displayBasket.value);
	}
});

watch(
	[basket, () => norceStore.storeContext.cultureCode, getIsLoading],
	async () => {
		if (
			basket.value &&
			Object.keys(basket.value).length &&
			!isSyncingCheckout.value &&
			!getIsLoading.value &&
			!isDeliveryRefreshing.value
		) {
			const currentCulture = norceStore.storeContext.cultureCode;
			const currentBasketId = basket.value?.Id;
			const localeChanged = currentCulture !== lastCultureCode;
			const basketSwitched = currentBasketId !== lastBasketId;

			lastCultureCode = currentCulture;
			lastBasketId = currentBasketId;

			if (localeChanged && !basketSwitched) {
				orderId.value = "";
				order.value = null;
				return;
			}

			if (localeChanged) {
				orderId.value = "";
				order.value = null;
			}

			const orderRefChanged = basket.value?.OrderReference !== orderId.value;

			if (
				!localeChanged &&
				!basketSwitched &&
				!orderRefChanged &&
				orderId.value &&
				order.value
			) {
				await emitCheckoutUpdate("cart");
				return;
			}

			await placeOrder();
			if (orderId.value) {
				await emitCheckoutUpdate("cart");
			}
		}
	}
);
</script>
